import { useCanvasStore } from '../store/canvas-store';
import { isPanelOwned, type Frame } from '../store/types';
import { selectedFrames } from '../store/selectors';
import {
  gridTidy, alignFrames, distributeFrames,
  type Pos, type AlignEdge, type DistributeAxis,
} from '../store/arrange';
import { shortId } from './dropDetect';

/** Frames currently selected, excluding panel-owned tool frames. */
function liveSelected(): Frame[] {
  const s = useCanvasStore.getState();
  return selectedFrames(s).filter((f) => !isPanelOwned(f));
}

/** Clone one frame at (nx, ny) via the matching create command; returns new id.
 *  Mirrors the single-frame Duplicate behavior so both paths stay in sync. */
function cloneFrame(f: Frame, nx: number, ny: number): string {
  const dispatch = useCanvasStore.getState().dispatch;
  const id = shortId();
  if (f.kind === 'tsxFree' || f.kind === 'tsxMain') {
    dispatch({ t: 'tsxFrame.create', id, x: nx, y: ny, w: f.w, title: f.title, tsx: f.tsx });
  } else if (f.kind === 'tsxInstance') {
    dispatch({ t: 'tsxInstance.create', id, mainId: f.mainId, x: nx, y: ny, propValues: { ...f.propValues }, w: f.w });
  } else if (f.kind === 'instance') {
    dispatch({ t: 'instance.create', id, mainId: f.mainId, x: nx, y: ny, slotValues: { ...f.slotValues } });
  } else {
    const html = f.kind === 'free' || f.kind === 'main' ? f.html : '';
    dispatch({ t: 'frame.create', id, x: nx, y: ny, w: f.w, title: f.title, html });
  }
  return id;
}

/** Delete the whole selection in one undo step. */
export function deleteSelection(): void {
  const ids = liveSelected().map((f) => f.id);
  if (!ids.length) return;
  useCanvasStore.getState().dispatch({ t: 'frame.delete', ids });
}

/** Clone every selected frame (offset +24,+24), reselect the clones, one undo. */
export function duplicateSelection(): void {
  const store = useCanvasStore.getState();
  const frames = liveSelected();
  if (!frames.length) return;
  store.beginTransaction('duplicate selection');
  try {
    const newIds = frames.map((f) => cloneFrame(f, f.x + 24, f.y + 24));
    store.dispatch({ t: 'select', ids: newIds });
  } finally {
    store.endTransaction();
  }
}

/** Apply target positions as a batch of frame.move commands in one undo step. */
function applyPositions(targets: Map<string, Pos>, label: string): void {
  if (targets.size === 0) return;
  const store = useCanvasStore.getState();
  const byId = new Map(store.doc.frames.map((f) => [f.id, f] as const));
  store.beginTransaction(label);
  try {
    for (const [id, pos] of targets) {
      const f = byId.get(id);
      if (!f) continue;
      const dx = pos.x - f.x;
      const dy = pos.y - f.y;
      if (dx !== 0 || dy !== 0) store.dispatch({ t: 'frame.move', ids: [id], dx, dy });
    }
  } finally {
    store.endTransaction();
  }
}

/** All arrangeable frames on the canvas (non-panel, non-note). */
function arrangeableAll(): Frame[] {
  return useCanvasStore.getState().doc.frames.filter((f) => !isPanelOwned(f) && f.kind !== 'note');
}

export function tidySelection(): void {
  applyPositions(gridTidy(liveSelected()), 'tidy selection');
}
export function tidyCanvas(): void {
  applyPositions(gridTidy(arrangeableAll()), 'tidy canvas');
}
export function alignSelection(edge: AlignEdge): void {
  applyPositions(alignFrames(liveSelected(), edge), `align ${edge}`);
}
export function distributeSelection(axis: DistributeAxis): void {
  applyPositions(distributeFrames(liveSelected(), axis), `distribute ${axis}`);
}

/** Select every non-panel frame on the canvas. */
export function selectAll(): void {
  const ids = useCanvasStore
    .getState()
    .doc.frames.filter((f) => !isPanelOwned(f))
    .map((f) => f.id);
  useCanvasStore.getState().dispatch({ t: 'select', ids });
}
